/* ====================================================
   ESAY shared primitives - pure Tailwind, no MUI.
   Public API matches the legacy MUI-based primitives so the
   28 consumers don't need shape changes:
   PageContainer, Section, PageHero, PageSubtitlePill, Card,
   EmptyState, Spinner, Skeleton, Brand, CTAButton, FormButton,
   Protected, CarouselFrame, InfoRow, Callout
   ==================================================== */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

/* ──────────────────────────────────────────────────────
   PageContainer - single max-width: 1120px on lg+.
   Backward-compat: accepts maxWidth ("xs"|"sm"|"md"|"lg"|"xl").
   ────────────────────────────────────────────────────── */
const MAXW = {
  xs: 'max-w-[600px]',
  sm: 'max-w-[800px]',
  md: 'max-w-[1000px]',
  lg: 'max-w-[1120px]',
  xl: 'max-w-[1320px]',
};
export function PageContainer({ children, maxWidth = 'lg', className = '', ...rest }) {
  return (
    <div
      className={`mx-auto w-full px-3 sm:px-5 lg:px-6 ${MAXW[maxWidth] || MAXW.lg} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Section - vertical block + optional kicker/title/subtitle/action.
   ────────────────────────────────────────────────────── */
export function Section({ kicker, title, subtitle, action, children, className = '', ...rest }) {
  return (
    <section className={`py-6 sm:py-8 ${className}`} {...rest}>
      {(kicker || title || subtitle || action) && (
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {kicker && (
              <div className="mb-1.5 inline-flex items-center gap-1.5">
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-60" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber" />
                </span>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber">{kicker}</span>
              </div>
            )}
            {title && (
              <h2 className="text-[20px] sm:text-[24px] md:text-[26px] font-bold leading-[1.18] tracking-[-0.018em] text-fg-primary">
                {title}
              </h2>
            )}
            {title && (
              <div aria-hidden className="mt-2 h-[2px] w-12 rounded-full bg-gradient-to-r from-amber to-amber/0" />
            )}
            {subtitle && (
              <p className="mt-2 max-w-[640px] text-[13px] leading-[1.6] text-fg-secondary">
                {subtitle}
              </p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ──────────────────────────────────────────────────────
   PageHero - centered hero with kicker + title + glow line + subtitle.
   compact = tighter padding; supports children below.
   ────────────────────────────────────────────────────── */
export function PageHero({ kicker, title, subtitle, children, compact, className = '', ...rest }) {
  return (
    <header
      className={`
        relative isolate overflow-hidden text-center
        ${compact ? 'pt-6 pb-3 sm:pt-7 sm:pb-3' : 'pt-10 pb-6 sm:pt-14 sm:pb-7'}
        ${className}
      `}
      {...rest}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 -top-32 -z-10 h-[420px] w-[640px] -translate-x-1/2"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(251,191,36,0.16), rgba(251,191,36,0.05) 35%, transparent 60%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent 10%, rgba(251,191,36,0.4) 50%, transparent 90%)',
        }}
      />

      <div className="relative z-10 mx-auto max-w-[760px] px-4">
        {kicker && (
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber/30 bg-amber/10 px-3 py-1">
            <span aria-hidden className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber" />
            </span>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber">{kicker}</span>
          </div>
        )}
        {title && (
          <h1
            className={`
              font-bold tracking-[-0.022em] text-fg-primary
              ${compact ? 'text-[24px] sm:text-[28px]' : 'text-[28px] sm:text-[36px] md:text-[42px]'}
              leading-[1.12]
            `}
          >
            {title}
          </h1>
        )}
        <div
          aria-hidden
          className="mx-auto my-4 h-px w-36 rounded-full"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(251,191,36,0.7), transparent)',
            boxShadow: '0 0 14px rgba(251,191,36,0.3)',
          }}
        />
        {subtitle && (
          <p className="mx-auto max-w-[560px] text-[13px] sm:text-[14px] leading-[1.65] text-fg-secondary">
            {subtitle}
          </p>
        )}
        {children}
      </div>
    </header>
  );
}

/* ──────────────────────────────────────────────────────
   PageSubtitlePill - small amber-outlined pill under heroes.
   ────────────────────────────────────────────────────── */
export function PageSubtitlePill({ emphasis, children, ...rest }) {
  return (
    <div className="text-center">
      <div
        className="
          mt-3 inline-flex items-center gap-2 rounded-full
          border border-amber/40 bg-surface-paper px-3.5 py-1.5
          text-[12.5px] font-medium text-fg-primary
          shadow-[0_0_0_1px_rgba(251,191,36,0.05)]
        "
        {...rest}
      >
        {emphasis && <strong className="text-amber font-bold">{emphasis}</strong>}
        <span className="text-fg-secondary">{children}</span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Card - base panel + interactive hover.
   Backwards compat: accepts data-interactive="true" for hover.
   className passes through.
   ────────────────────────────────────────────────────── */
export const Card = React.forwardRef(function Card(
  { children, className = '', ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={`
        rounded-2xl border border-amber/18 bg-surface-paper
        shadow-card
        transition-[border-color,transform,box-shadow] duration-200 ease-out-soft
        data-[interactive=true]:hover:border-amber/55
        data-[interactive=true]:hover:-translate-y-0.5
        data-[interactive=true]:hover:shadow-card-hover
        data-[interactive=true]:cursor-pointer
        ${className}
      `}
      {...rest}
    >
      {children}
    </div>
  );
});

/* ──────────────────────────────────────────────────────
   EmptyState - icon + title + subtitle + optional action.
   ────────────────────────────────────────────────────── */
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      {icon && (
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber/10 text-amber">
          {icon}
        </div>
      )}
      {title && <h3 className="text-[16px] font-bold tracking-[-0.01em] text-fg-primary">{title}</h3>}
      {subtitle && (
        <p className="mt-1.5 max-w-[420px] text-[13px] leading-[1.6] text-fg-secondary">{subtitle}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Spinner - sizes sm | md | lg or numeric px.
   ────────────────────────────────────────────────────── */
const SPIN_SIZE = { sm: 16, md: 24, lg: 32 };
export function Spinner({ size = 'md', className = '', ...rest }) {
  const px = typeof size === 'number' ? size : (SPIN_SIZE[size] || 24);
  return (
    <svg
      viewBox="0 0 24 24"
      width={px}
      height={px}
      className={`animate-spin text-amber ${className}`}
      aria-hidden
      {...rest}
    >
      <circle
        cx="12" cy="12" r="10"
        stroke="currentColor" strokeWidth="2.5" fill="none"
        strokeDasharray="40 60" strokeLinecap="round"
      />
    </svg>
  );
}

/* ──────────────────────────────────────────────────────
   Skeleton - shimmer placeholder. variant: text|rect|circle.
   ────────────────────────────────────────────────────── */
export function Skeleton({ variant = 'rect', width, height, className = '', style, ...rest }) {
  const shape = variant === 'circle' ? 'rounded-full' : variant === 'text' ? 'rounded' : 'rounded-md';
  const w = width != null ? (typeof width === 'number' ? `${width}px` : width) : '100%';
  const h = height != null
    ? (typeof height === 'number' ? `${height}px` : height)
    : (variant === 'text' ? '0.9em' : '100%');
  return (
    <div
      className={`relative overflow-hidden bg-surface-overlay ${shape} ${className}`}
      style={{ width: w, height: h, ...style }}
      {...rest}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
          animation: 'esay-shimmer 1.6s linear infinite',
        }}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Brand - wordmark/logo image.
   ────────────────────────────────────────────────────── */
export function Brand({ size = 40 }) {
  return (
    <span className="inline-flex items-center gap-2 font-bold tracking-tight text-fg-primary">
      <img src="/assets/ESAY.png" alt="ESAY" style={{ height: size, width: size, objectFit: 'contain' }} />
    </span>
  );
}

/* ──────────────────────────────────────────────────────
   CTAButton - amber pill, optional loading + start/end icons.
   Supports `component={RouterLink}` + `to=` for link variant
   (backwards compat with old MUI button API).
   ────────────────────────────────────────────────────── */
const CTA_SIZE = {
  small:  'h-9 px-3.5 text-[11px]',
  medium: 'h-11 px-5  text-[12px]',
  large:  'h-12 px-6  text-[12.5px]',
};
export function CTAButton({
  loading,
  children,
  startIcon,
  endIcon,
  className = '',
  size = 'medium',
  variant,           // unused, kept for API compat
  color,             // unused, kept for API compat
  fullWidth,
  component,
  to,
  href,
  type = 'button',
  disabled,
  onClick,
  ...rest
}) {
  const cls = `
    group inline-flex items-center justify-center gap-1.5 rounded-lg
    ${CTA_SIZE[size] || CTA_SIZE.medium}
    font-bold uppercase tracking-[0.1em]
    bg-amber text-ink
    transition-[background-color,box-shadow,transform] duration-200 ease-out-soft
    hover:bg-amber-light
    hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_8px_22px_-8px_rgba(251,191,36,0.5)]
    active:scale-[0.98]
    disabled:opacity-60 disabled:pointer-events-none
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;

  const inside = (
    <>
      {loading ? <Spinner size="sm" className="text-ink" /> : startIcon}
      <span>{children}</span>
      {!loading && endIcon}
    </>
  );

  if (component === RouterLink || (to && !href)) {
    return (
      <RouterLink to={to} className={cls} onClick={onClick} {...rest}>
        {inside}
      </RouterLink>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} onClick={onClick} {...rest}>
        {inside}
      </a>
    );
  }
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled || loading} {...rest}>
      {inside}
    </button>
  );
}

/* ──────────────────────────────────────────────────────
   FormButton - same height as 40px form inputs (filter rows etc.).
   ────────────────────────────────────────────────────── */
export function FormButton({ children, className = '', component, to, href, type = 'button', onClick, ...rest }) {
  const cls = `
    inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5
    text-[12px] font-bold uppercase tracking-[0.08em]
    bg-amber text-ink whitespace-nowrap min-w-[100px]
    transition-[background-color,box-shadow] duration-200
    hover:bg-amber-light
    hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_6px_18px_-8px_rgba(251,191,36,0.5)]
    ${className}
  `;
  if (component === RouterLink || (to && !href)) {
    return <RouterLink to={to} className={cls} onClick={onClick} {...rest}>{children}</RouterLink>;
  }
  if (href) {
    return <a href={href} className={cls} onClick={onClick} {...rest}>{children}</a>;
  }
  return <button type={type} className={cls} onClick={onClick} {...rest}>{children}</button>;
}

/* ──────────────────────────────────────────────────────
   Protected - userSelect: none wrapper for content pages.
   (Right-click MUST stay enabled - see memory.)
   ────────────────────────────────────────────────────── */
export function Protected({ children, className = '', ...rest }) {
  return (
    <div
      className={`select-none ${className}`}
      style={{ WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   LoginRequired - centered "members only" gate card.
   Used to fully hide protected detail pages (Single*) instead of
   the previous blur-tease, which left content readable.
   ────────────────────────────────────────────────────── */
export function LoginRequired({
  title = 'Sign in required',
  message = 'This content is available to ESAY members. Sign in to continue.',
  onLogin,
}) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-10">
      <div
        className="
          relative w-full max-w-md overflow-hidden
          rounded-2xl border border-amber/24 bg-surface-paper
          p-7 sm:p-8 text-center
          shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)]
        "
      >
        <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber/60 to-transparent" />

        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber/12 text-amber">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7" aria-hidden>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2 className="text-[19px] sm:text-[21px] font-bold tracking-[-0.02em] text-fg-primary leading-tight">
          {title}
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.55] text-fg-secondary">
          {message}
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
          {onLogin ? (
            <button
              type="button"
              onClick={onLogin}
              className="
                inline-flex h-10 items-center justify-center rounded-full px-5
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-all duration-200 ease-out-soft
                hover:bg-amber-light
                hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                active:scale-[0.98]
              "
            >
              Sign in
            </button>
          ) : (
            <RouterLink
              to="/login"
              className="
                inline-flex h-10 items-center justify-center rounded-full px-5
                text-[12px] font-bold uppercase tracking-[0.1em]
                bg-amber text-ink
                transition-all duration-200 ease-out-soft
                hover:bg-amber-light
                hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_8px_22px_-8px_rgba(251,191,36,0.5)]
                active:scale-[0.98]
              "
            >
              Sign in
            </RouterLink>
          )}
          <RouterLink
            to="/signup"
            className="
              inline-flex h-10 items-center justify-center rounded-full px-5
              text-[12px] font-bold uppercase tracking-[0.1em]
              border border-amber/40 text-amber
              transition-[background-color,border-color,color,box-shadow] duration-200 ease-out-soft
              hover:bg-amber/10 hover:border-amber
            "
          >
            Create account
          </RouterLink>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   CarouselFrame - wraps react-responsive-carousel; styles dots + non-active.
   ────────────────────────────────────────────────────── */
export function CarouselFrame({ children, className = '', ...rest }) {
  return (
    <div
      className={`esay-carousel-frame relative ${className}`}
      {...rest}
    >
      {children}
      <style>{`
        .esay-carousel-frame .carousel .slide {
          background: transparent;
          opacity: 0.35;
          transform: scale(0.92);
          transition: opacity 300ms cubic-bezier(0.16,1,0.3,1), transform 300ms cubic-bezier(0.16,1,0.3,1);
        }
        .esay-carousel-frame .carousel .slide.selected {
          opacity: 1;
          transform: scale(1);
        }
        .esay-carousel-frame .carousel .control-dots {
          padding: 8px 0;
          margin-bottom: 0;
        }
        .esay-carousel-frame .carousel .control-dots .dot {
          background: #8A8A84;
          box-shadow: none;
          opacity: 0.4;
          width: 6px; height: 6px;
          margin: 0 3px;
          transition: all 200ms cubic-bezier(0.16,1,0.3,1);
        }
        .esay-carousel-frame .carousel .control-dots .dot.selected {
          background: #FBBF24;
          opacity: 1;
          width: 22px;
          border-radius: 9999px;
        }
        .esay-carousel-frame .carousel-status { display: none; }
      `}</style>
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   InfoRow - key/value row used on profile, details, subscriptions.
   ────────────────────────────────────────────────────── */
export function InfoRow({ label, value, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-3 py-3 border-b border-border-subtle last:border-b-0">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary leading-[1.4]">
        {label}
      </div>
      <div className="text-[14px] font-medium text-fg-primary break-words">
        {value ?? children ?? <span className="text-fg-muted">-</span>}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────
   Callout - informational card with amber left bar + icon + title + body.
   ────────────────────────────────────────────────────── */
export function Callout({ icon, title, children, action }) {
  return (
    <div className="
      relative overflow-hidden rounded-2xl border border-amber/24 bg-amber/5
      p-4 sm:p-5
      flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4
    ">
      <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber" />
      {icon && (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber/15 text-amber">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        {title && <h3 className="text-[14px] font-bold text-fg-primary">{title}</h3>}
        {children && (
          <div className="mt-1 text-[12.5px] leading-[1.6] text-fg-secondary [&_*]:text-fg-secondary [&_strong]:text-amber [&_strong]:font-bold">
            {children}
          </div>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </div>
  );
}
